import { Button } from '@/shared/components/shadcn/button';
import { cn } from '@/shared/utils';
import { Search, Square } from 'lucide-react';

export function StoppedSearchEmptyState() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 py-16 text-center">
      <div className="flex w-100 flex-col items-center gap-3">
        <div className="bg-muted text-foreground flex size-11 items-center justify-center rounded-full">
          <Search className="size-5" />
        </div>
        <h3 className="text-foreground text-base font-semibold">Search stopped</h3>
        <span className="text-muted-foreground text-sm">
          Write a new prompt to start another search.
        </span>
      </div>
    </div>
  );
}

export function DiscoveryNoResults() {
  return (
    <div className="border-border bg-background rounded-lg border p-6 text-center">
      <p className="text-foreground text-sm font-semibold">No creators found</p>
      <p className="text-muted-foreground mt-1 text-sm">
        Try a broader prompt or loosen the selected filters.
      </p>
    </div>
  );
}

export function DiscoveryResultsError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="border-border bg-background rounded-lg border p-6 text-center">
      <p className="text-foreground text-sm font-semibold">Could not load creator results</p>
      <p className="text-muted-foreground mt-1 text-sm">
        Keep the current discovery open and try loading the results again.
      </p>
      <Button className="mt-4" variant="outline" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

export function DiscoveryIncrementalState({ label }: { label: string }) {
  return (
    <div className="border-border bg-background self-center rounded-md border px-4 py-2">
      <p className="text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export function PreFirstBatchGuidance({ onStopClick }: { onStopClick: () => void }) {
  return (
    <div className="border-border/90 bg-muted flex items-center justify-between gap-3 rounded-xl border px-4 py-3.5">
      <div className="flex min-w-0 items-center gap-3">
        <div className="border-border bg-card relative flex size-10 shrink-0 items-center justify-center rounded-full border">
          <span className="bg-muted-foreground/70 animate-search-orbit absolute size-2 rounded-full" />
          <Search className="text-foreground size-5" />
        </div>
        <div className="min-w-0">
          <p className="text-foreground text-sm font-semibold">
            We&apos;ll show creators as we find them
          </p>
          <p className="text-muted-foreground mt-0.5 text-sm">
            New results will keep appearing here as the search runs.
          </p>
        </div>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onStopClick}>
        <Square className="text-destructive size-3.5 fill-current" />
        Stop search
      </Button>
    </div>
  );
}

export interface NoCreatorsResultsProps {
  className?: string;
}

export function NoCreatorsResults({ className }: NoCreatorsResultsProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center pt-16 text-center', className)}>
      <div className="flex w-100 flex-col gap-3">
        <h3 className="text-foreground text-base font-semibold">No creators found</h3>
        <span className="text-muted-foreground text-sm">
          Try broadening your prompt or adjusting filters to find more creators.
        </span>
      </div>
    </div>
  );
}
